import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AppRoute, buildRoute } from 'appRoutes'
import FullpageLoader from 'components/Loader/FullpageLoader/FullpageLoader'
import { randomRuleId } from 'features/rulesEngine'
import { useRulesetCreateMutation, useTemplatesList } from 'hooks/api/rulesets'
import { useCompassToast } from 'hooks/toast'
import { useEffect, useMemo, useRef } from 'react'

export const Route = createFileRoute('/_authenticated/workspaces/$subscriptionId/rulesets/templates/$templateSlug')({
  component: TemplateRouteHandler,
})

function TemplateRouteHandler() {
  const { subscriptionId, templateSlug } = Route.useParams()
  const navigate = useNavigate()
  const { showToast } = useCompassToast()
  const { data: templates, isLoading, error } = useTemplatesList()
  const { mutateAsync: createRuleset } = useRulesetCreateMutation()
  const hasStarted = useRef(false)

  const template = useMemo(() => templates?.find((item) => item.slug === templateSlug), [templates, templateSlug])

  useEffect(() => {
    if (hasStarted.current || isLoading) {
      return
    }

    if (error) {
      showToast({
        message: error.message ?? 'Failed to load templates. Please try again.',
        severity: 'error',
      })
      navigate({
        to: buildRoute(AppRoute.RulesetsListing, { subscriptionId }),
        replace: true,
      })
      return
    }

    if (!template) {
      showToast({
        message: 'Template not found.',
        severity: 'error',
      })
      navigate({
        to: buildRoute(AppRoute.RulesetsListing, { subscriptionId }),
        replace: true,
      })
      return
    }

    hasStarted.current = true

    createRuleset({
      params: { subscriptionId },
      data: {
        name: template.ruleset.name,
        enabled: template.ruleset.enabled,
        rules: template.ruleset.rules.map((rule) => ({
          ...rule,
          id: randomRuleId(),
        })),
      },
    })
      .then((ruleset) => {
        navigate({
          to: AppRoute.RulesetPage,
          params: { subscriptionId, rulesetId: ruleset.id },
          replace: true,
        })
      })
      .catch((createError: any) => {
        hasStarted.current = false
        showToast({
          message: createError.message ?? 'Failed to create template ruleset. Please try again.',
          severity: 'error',
        })
        navigate({
          to: buildRoute(AppRoute.RulesetsListing, { subscriptionId }),
          replace: true,
        })
      })
  }, [createRuleset, error, isLoading, navigate, template, showToast, subscriptionId])

  return <FullpageLoader testId='template-route-loader' />
}
